import { Children, type ComponentPropsWithRef, type ElementType, type ForwardedRef, forwardRef } from 'react';

import clsx from 'clsx';

import styles from './Link.module.scss';

type LinkProps<E extends ElementType = 'a'> = ComponentPropsWithRef<E> &
  ElementType<HTMLElement> & {
    as?: E;
  } & (
    | {
        asButton?: false;
        variant?: 'primary' | 'tonal' | 'monochrome';
      }
    | {
        asButton: true;
        variant?: 'filled' | 'outlined' | 'text' | 'error' | 'error-outlined' | 'tonal';
      }
  );

export const Link = forwardRef<ElementType<HTMLElement>, LinkProps<ElementType>>(
  (
    { as, children, className, href = '', asButton = false, variant = 'primary', ...linkProps }: LinkProps<ElementType>,
    ref: ForwardedRef<ElementType<HTMLElement>>,
  ) => {
    const Component = as || 'a';
    // need to see if there is any text to send to css as css cant see text nodes
    let containsText = false;
    Children.forEach(children, (child) => {
      if (typeof child === 'string') {
        containsText = true;
      }
    });

    return (
      <Component
        className={clsx(
          styles.link,
          {
            // text styling
            [styles.text]: !asButton,
            [styles['text-primary']]: variant === 'primary' && !asButton,
            [styles['text-tonal']]: variant === 'tonal' && !asButton,
            [styles['text-monochrome']]: variant === 'monochrome' && !asButton,
            // button look styling
            [styles['contains-text']]: containsText,
            [styles.button]: asButton,
            [styles['variant-filled']]: variant === 'filled' && asButton,
            [styles['variant-outlined']]: variant === 'outlined' && asButton,
            [styles['variant-text']]: variant === 'text' && asButton,
            [styles['variant-error']]: variant === 'error' && asButton,
            [styles['variant-error-outlined']]: variant === 'error-outlined' && asButton,
            [styles['variant-tonal']]: variant === 'tonal' && asButton,
          },
          className,
        )}
        href={href}
        ref={ref}
        {...linkProps}
      >
        {children}
      </Component>
    );
  },
);
